import React, { useContext } from 'react'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import { multiValidate, notEmpty, minMaxFields } from 'helpers/validators'

import { ContentBlock, ExpandableBlocks } from 'components/ContentBlock'
import { Form, Text, Number as NumberInput } from 'components/forms'
import Button from 'components/Button'
import { useMutation } from 'components/graphql'
import { ConfirmModal } from 'components/Modal'
import { RefreshSideListContext } from 'components/SectionHandler/context/RefreshSideListContext'

import SeriesSetting from './SeriesSetting'
import updateRangeLoyaltyCards from '../../gql/updateRangeLoyaltyCards'
import createRangeLoyaltyCards from '../../gql/createRangeLoyaltyCards'
import resetRangeLoyaltyCards from '../../gql/resetRangeLoyaltyCards'

const Series = () => {
	const [t] = useTranslation()
	const [updateRange] = useMutation(updateRangeLoyaltyCards, {
		onCompleted: () => {
			setRefreshSideList(true)
			setRefreshItemData(true)
		}
	})
	const [createRange] = useMutation(createRangeLoyaltyCards, {
		onCompleted: () => {
			setRefreshSideList(true)
		}
	})
	const [resetRange] = useMutation(resetRangeLoyaltyCards, {
		onCompleted: () => {
			setRefreshSideList(true)
		}
	})
	const { setRefreshSideList, setRefreshItemData } = useContext(RefreshSideListContext)

	const handleSubmit = (mutationType, modalText) => vals => {
		ConfirmModal({
			text: `${modalText} ${t('common.strings.from')} ${vals.ids.from} ${t('common.strings.to')} ${vals.ids.to}`
		}).then(() => {
			switch (mutationType) {
				case 'create': createRange({ variables: vals })
					break
				case 'update': updateRange({ variables: vals })
					break
				case 'reset': resetRange({ variables: vals })
					break
				default:
			}
		}, rejected => {})
	}
	const initialValuesLoad = {
		transactionFields: { description: t('loyalty_card_section.strings.load_prepaid_cards') }
	}
	const initialValuesReset = {
		transactionFields: { description: t('loyalty_card_section.strings.zeroing') }
	}

	return(
		<ExpandableBlocks>

			<ContentBlock label={t('common.buttons.create')} startExpanded additionalSwitcher>
				<ExpandableBlocks nested>

					<ContentBlock label={t('common.strings.card')} startExpanded>
						<Row>
							<Col xs>
								<Form onSubmit={vals => handleSubmit('create', t('loyalty_card_section.strings.create_loyalty_cards'))({ ...vals, fields: { ...vals.fields, type: 2 } })}>
									<p className='form-group-label'>{t('loyalty_card_section.strings.loyalty_card_plural')}</p>
									<NumberInput
										field='ids.from'
										label={t('common.strings.from')}
										placeholder={t('common.strings.from')}
										{...multiValidate([notEmpty(), minMaxFields('min', 'ids.to')])}
										inline
										shrink
										withControls={false}
									/>
									<NumberInput
										field='ids.to'
										label={t('common.strings.to')}
										placeholder={t('common.strings.to')}
										{...multiValidate([notEmpty(), minMaxFields('max', 'ids.from')])}
										inline
										shrink
										withControls={false}
									/>
									<Button text={t('loyalty_card_section.strings.create_loyalty_cards')} type='submit' />
								</Form>
							</Col>
							<Col xs>
								<Form onSubmit={vals => handleSubmit('create', t('loyalty_card_section.strings.create_inhouse_cards'))({ ...vals, fields: { ...vals.fields, type: 1 } })}>
									<p className='form-group-label'>{t('loyalty_card_section.strings.inhouse_card_plural')}</p>
									<NumberInput
										field='ids.from'
										label={t('common.strings.from')}
										placeholder={t('common.strings.from')}
										{...multiValidate([notEmpty(), minMaxFields('min', 'ids.to')])}
										inline
										shrink
										withControls={false}
									/>
									<NumberInput
										field='ids.to'
										label={t('common.strings.to')}
										placeholder={t('common.strings.to')}
										{...multiValidate([notEmpty(), minMaxFields('max', 'ids.from')])}
										inline
										shrink
										withControls={false}
									/>
									<Button text={t('loyalty_card_section.strings.create_inhouse_cards')} type='submit' />
								</Form>
							</Col>
						</Row>
					</ContentBlock>

					<ContentBlock label={`${t('loyalty_card_section.strings.load_capital')} / ${t('loyalty_card_section.strings.label_capital')}`}>
						<Row>
							<Col xs>
								<Form onSubmit={handleSubmit('update', t('loyalty_card_section.strings.load_prepaid_cards'))} initialValues={initialValuesLoad}>
									<p className='form-group-label'>{t('loyalty_card_section.strings.load_prepaid_cards')}</p>
									<NumberInput
										field='ids.from'
										label={t('common.strings.from')}
										placeholder={t('common.strings.from')}
										{...multiValidate([notEmpty(), minMaxFields('min', 'ids.to')])}
										inline
										shrink
										withControls={false} />
									<NumberInput
										field='ids.to'
										label={t('common.strings.to')}
										placeholder={t('common.strings.to')}
										{...multiValidate([notEmpty(), minMaxFields('max', 'ids.from')])}
										inline
										shrink
										withControls={false} />
									<NumberInput
										field='fields.balance'
										label={t('loyalty_card_section.fields.balance')}
										{...notEmpty()}
										enableNegative
										withControls={false}
										shrink />
									<Text field='transactionFields.description' label={t('common.fields.name')} />
									<Button text={t('loyalty_card_section.strings.load_prepaid_cards')} type='submit' />
								</Form>
							</Col>
							<Col xs>
								<Form onSubmit={handleSubmit('update', t('loyalty_card_section.strings.label_prepaid_cards'))}>
									<p className='form-group-label'>{t('loyalty_card_section.strings.label_prepaid_cards')}</p>
									<NumberInput
										field='ids.from'
										label={t('common.strings.from')}
										placeholder={t('common.strings.from')}
										{...multiValidate([notEmpty(), minMaxFields('min', 'ids.to')])}
										inline
										shrink
										withControls={false}
									/>
									<NumberInput
										field='ids.to'
										label={t('common.strings.to')}
										placeholder={t('common.strings.to')}
										{...multiValidate([notEmpty(), minMaxFields('max', 'ids.from')])}
										inline
										shrink
										withControls={false}
									/>
									<Text
										field='fields.name'
										label={t('loyalty_card_section.fields.card_holders')}
										{...notEmpty()}
									/>
									<Button text={t('loyalty_card_section.strings.label_prepaid_cards')} type='submit' />
								</Form>
							</Col>
						</Row>
					</ContentBlock>

				</ExpandableBlocks>
			</ContentBlock>

			<SeriesSetting onSubmit={handleSubmit('update', `${t('loyalty_card_section.strings.settings')} ${t('common.buttons.apply')}`)} />

			<ContentBlock label={t('loyalty_card_section.strings.reset_booking')}>
				<Form onSubmit={handleSubmit('reset', t('loyalty_card_section.strings.reset_booking'))} initialValues={initialValuesReset}>
					<Row>
						<Col xs={6}>
							<div><Text field='transactionFields.description' hidden /></div>
							<NumberInput
								field='ids.from'
								placeholder={t('common.strings.from')}
								{...multiValidate([notEmpty(), minMaxFields('min', 'ids.to')])}
								inline
								shrink
								withControls={false}
								label={t('common.strings.from')}
							/>
							<NumberInput
								field='ids.to'
								label={t('common.strings.to')}
								placeholder={t('common.strings.to')}
								{...multiValidate([notEmpty(), minMaxFields('max', 'ids.from')])}
								inline
								shrink
								withControls={false}
							/>
							<div>
								<Button text={t('loyalty_card_section.strings.reset_booking')} type='submit' />
							</div>
						</Col>
					</Row>
				</Form>
			</ContentBlock>

		</ExpandableBlocks>
	)
}

export default Series
